import { ref, computed } from 'vue';
import type { FilterConfiguration } from '@/components/base/dynamicFilterBuilder';
import { useI18n } from '@/composables/useI18n';
import { useAbortController } from "@/composables/useAbortController";
import type { DeviceWithCustomer, FetchDevicesParam } from '@/components/devices/scripts/types';
import { fetchDevices } from '@/components/devices/scripts/deviceApiRequests';

/**
 * Options for configuring the device filter.
 */
interface DeviceFilterOptions {
  /**
   * Callback function to be called when an error occurs.
   * @param error - The error object.
   */
  onError?: (error: unknown) => void;
}

/**
 * Composable for creating and managing a device filter.
 * @param options - Configuration options for the device filter.
 * @returns An object containing the device filter configuration and the selected device.
 */
export const useDeviceFilter = (options: DeviceFilterOptions) => {
  const { t } = useI18n();
  const { getNewAbortController } = useAbortController();
  const selectedDevice = ref<DeviceWithCustomer | null>(null);

  // Create a refresh trigger ref
  const refreshTrigger = ref(0);

  const getFilteredItems = async (searchInput: string | null) => {
    const fetchArgs: FetchDevicesParam = {
      pageSize: 1000, // Get all devices
    };

    if (searchInput) fetchArgs.name = searchInput;

    const { error, response } = await fetchDevices(fetchArgs, getNewAbortController().signal);

    if (error) {
      options?.onError?.(error);
      return [];
    }

    const result = (response || []).sort((a, b) => a.name.localeCompare(b.name));

    return result;
  }

  /**
   * Creates a filter configuration for selecting devices.
   * @param config - Configuration for the filter including column properties, events, and model.
   * @returns The filter configuration object.
   */
  const createDeviceFilterConfiguration = (
    config?: Pick<FilterConfiguration, 'colProps' | 'events' | 'debounce' | 'inAdvancedSection'>
  ): FilterConfiguration => {
    const filterConfig: FilterConfiguration = {
      ...(config ?? {}),
      type: "combobox",
      props: computed(() => ({
        label: t("MerchantTransactionManagement.device_selection_field"),
        "item-text": "name",
        placeholder: t("MerchantTransactionManagement.device_selection_field"),
      })),
      model: selectedDevice,
      getFilteredItems: getFilteredItems,
      inAdvancedSection: config?.inAdvancedSection,
      refreshTrigger: refreshTrigger
    };

    return filterConfig;
  };

  const resolveSelectedDevice = async (id: number | null) => {
    if (id == null) {
      selectedDevice.value = null;
      return;
    }

    const { error, response } = await fetchDevices({ pageSize: 1000 }, getNewAbortController().signal);
    if (error) {
      options.onError?.(error);
      return;
    }

    selectedDevice.value = (response ?? []).find(device => device.id === id) ?? null;
  };

  return {
    createDeviceFilterConfiguration,
    selectedDevice,
    resolveSelectedDevice,
  };
};
